/**
 * Feedback taxonomy for the whole-post studio.
 *
 * Users react to generated caption + image candidates (and to drafts they
 * reject in the queue) with a 👍/👎 and a REASON drawn from a fixed taxonomy.
 * Codes are stored on the FeedbackRecord; labels are for the UI. The adaptive
 * generator (Phase 3d) reads recent feedback to bias future prompts — fast,
 * but never overriding the brand brain's safety rails.
 *
 * Contract: a DISLIKE must carry at least one reason (that's where the learning
 * value is); a LIKE may carry an optional reason. Reasons are validated
 * server-side against the (target, sentiment) list — the client is never
 * trusted to send an arbitrary code.
 */

export const FEEDBACK_TARGETS = ["caption", "image"] as const;
export type FeedbackTarget = (typeof FEEDBACK_TARGETS)[number];

export const FEEDBACK_SENTIMENTS = ["like", "dislike"] as const;
export type FeedbackSentiment = (typeof FEEDBACK_SENTIMENTS)[number];

export interface FeedbackReason {
  code: string;
  label: string;
}

/** Image 👎 — what's wrong with a generated image. */
export const IMAGE_DISLIKE_REASONS: readonly FeedbackReason[] = [
  { code: "off_brand_look", label: "Off-brand look" },
  { code: "wrong_subject", label: "Wrong subject" },
  { code: "weak_composition", label: "Weak composition" },
  { code: "wrong_mood", label: "Wrong mood/vibe" },
  { code: "low_quality", label: "Low quality/artifacts" },
];

/** Image 👍 — what's right about a generated image. */
export const IMAGE_LIKE_REASONS: readonly FeedbackReason[] = [
  { code: "on_brand", label: "On-brand" },
  { code: "right_subject", label: "Right subject" },
  { code: "great_composition", label: "Great composition" },
  { code: "perfect_mood", label: "Perfect mood" },
];

/** Caption 👎 — what's wrong with a generated caption. */
export const CAPTION_DISLIKE_REASONS: readonly FeedbackReason[] = [
  { code: "wrong_tone", label: "Wrong tone/voice" },
  { code: "too_long", label: "Too long" },
  { code: "too_short", label: "Too short" },
  { code: "weak_hook", label: "Weak hook" },
  { code: "wrong_cta", label: "Wrong CTA" },
  { code: "off_message", label: "Off-message/inaccurate" },
];

/** Caption 👍 — what's right about a generated caption. */
export const CAPTION_LIKE_REASONS: readonly FeedbackReason[] = [
  { code: "on_brand_voice", label: "On-brand voice" },
  { code: "strong_hook", label: "Strong hook" },
  { code: "right_length", label: "Right length" },
  { code: "good_cta", label: "Good CTA" },
];

/** The reason list for a (target, sentiment) pair. */
export function reasonsFor(
  target: FeedbackTarget,
  sentiment: FeedbackSentiment,
): readonly FeedbackReason[] {
  if (target === "image") {
    return sentiment === "dislike" ? IMAGE_DISLIKE_REASONS : IMAGE_LIKE_REASONS;
  }
  return sentiment === "dislike" ? CAPTION_DISLIKE_REASONS : CAPTION_LIKE_REASONS;
}

/** The set of allowed reason codes for a (target, sentiment) pair. */
export function validReasonCodes(
  target: FeedbackTarget,
  sentiment: FeedbackSentiment,
): Set<string> {
  return new Set(reasonsFor(target, sentiment).map((r) => r.code));
}

/** Human label for a stored reason code (falls back to the code). */
export function reasonLabel(
  target: FeedbackTarget,
  sentiment: FeedbackSentiment,
  code: string,
): string {
  return reasonsFor(target, sentiment).find((r) => r.code === code)?.label ?? code;
}

export function isFeedbackTarget(v: unknown): v is FeedbackTarget {
  return typeof v === "string" && (FEEDBACK_TARGETS as readonly string[]).includes(v);
}

export function isFeedbackSentiment(v: unknown): v is FeedbackSentiment {
  return typeof v === "string" && (FEEDBACK_SENTIMENTS as readonly string[]).includes(v);
}

// ─── Adaptive generation (Phase 3d): feedback → prompt guidance ────────

/** The minimal shape `summarizeFeedback` reads. `FeedbackRecord` (from the db
 *  module) is structurally assignable, so callers pass records directly without
 *  this module importing the db layer (which would create a cycle). */
export interface FeedbackSignal {
  target: FeedbackTarget;
  sentiment: FeedbackSentiment;
  reasons: string[];
  captionText?: string;
}

export interface LearnedPreferences {
  /** Recent liked caption texts → fed to generateDrafts' pastWinners bias. */
  likedCaptions: string[];
  /** Prefer/avoid guidance for the caption prompt ("" when no clear pattern). */
  captionGuidance: string;
  /** Prefer/avoid guidance for the shot director ("" when no clear pattern). */
  imageGuidance: string;
  /**
   * Human-readable alerts when recent feedback FIGHTS the brand brain (the
   * generated content follows the brand but keeps getting rejected as
   * off-brand) or contradicts itself. The studio surfaces these so the user
   * can update the brand brain rather than the loop silently adapting against
   * it — the user's "alert me on conflicts" requirement. Empty when none.
   */
  conflicts: string[];
}

/** A reason must recur this many times to count as a brand-vs-feedback conflict
 *  (a stronger bar than a prefer/avoid nudge — it suggests the brain itself). */
const CONFLICT_COUNT = 3;

/** Liked-caption exemplars to carry as pastWinners. */
const MAX_WINNERS = 5;
/**
 * A reason must recur this many times in the recent window before it becomes a
 * prefer/avoid instruction — so generation adapts fast to a real pattern but
 * not to a single stray click. Centralized so the "how adaptive" knob is one
 * place. Liked captions are exempt (a single strong like is a useful exemplar).
 */
const MIN_REASON_COUNT = 2;

/**
 * Summarize recent feedback (newest-first) into generation guidance. "Fast /
 * quickly adaptive": only the recent window passed in is considered, so newer
 * reactions dominate. Pure + browser-safe (no IO) — callable from the API or
 * a future UI preview.
 */
export function summarizeFeedback(signals: FeedbackSignal[]): LearnedPreferences {
  const likedCaptions: string[] = [];
  const counts: Record<FeedbackTarget, Record<FeedbackSentiment, Map<string, number>>> = {
    caption: { like: new Map(), dislike: new Map() },
    image: { like: new Map(), dislike: new Map() },
  };

  for (const s of signals) {
    if (
      s.target === "caption" &&
      s.sentiment === "like" &&
      s.captionText &&
      likedCaptions.length < MAX_WINNERS &&
      !likedCaptions.includes(s.captionText)
    ) {
      likedCaptions.push(s.captionText);
    }
    const bucket = counts[s.target][s.sentiment];
    for (const code of s.reasons) bucket.set(code, (bucket.get(code) ?? 0) + 1);
  }

  const topLabels = (
    m: Map<string, number>,
    target: FeedbackTarget,
    sentiment: FeedbackSentiment,
  ): string[] =>
    [...m.entries()]
      .filter(([, n]) => n >= MIN_REASON_COUNT)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([code]) => reasonLabel(target, sentiment, code).toLowerCase());

  const guidance = (target: FeedbackTarget): string => {
    const prefer = topLabels(counts[target].like, target, "like");
    const avoid = topLabels(counts[target].dislike, target, "dislike");
    const lines: string[] = [];
    if (prefer.length) lines.push(`Prefer: ${prefer.join(", ")}.`);
    if (avoid.length) lines.push(`Avoid: ${avoid.join(", ")}.`);
    return lines.join(" ");
  };

  const count = (target: FeedbackTarget, sentiment: FeedbackSentiment, code: string): number =>
    counts[target][sentiment].get(code) ?? 0;

  // Conflict detection: a generation follows the brand brain, so when the team
  // repeatedly rejects it AS off-brand, the brain — not the generator — is the
  // thing out of step. Surface that rather than silently adapting against it.
  const conflicts: string[] = [];
  if (
    count("caption", "dislike", "wrong_tone") + count("caption", "dislike", "off_message") >=
    CONFLICT_COUNT
  ) {
    conflicts.push(
      "Captions are repeatedly flagged for tone/voice even though they follow your brand brain — your brand voice settings may be out of date. Review the brand brain.",
    );
  }
  if (count("image", "dislike", "off_brand_look") >= CONFLICT_COUNT) {
    conflicts.push(
      "Images are repeatedly flagged as off-brand — your brand's visual style settings may need a refresh. Review the brand brain.",
    );
  }
  if (
    count("caption", "dislike", "too_long") >= CONFLICT_COUNT &&
    count("caption", "dislike", "too_short") >= CONFLICT_COUNT
  ) {
    conflicts.push(
      "Reviewers disagree on caption length (both “too long” and “too short” are recurring) — the loop can’t resolve this on its own.",
    );
  }

  return {
    likedCaptions,
    captionGuidance: guidance("caption"),
    imageGuidance: guidance("image"),
    conflicts,
  };
}
